// 3D-web course demo: the controls and the code are the same thing.
//
// The other courses argue "write the rule once". This one argues something the
// visitor can only really get by touching it — that a 3D website is not a video
// of a scene, it is a scene described in code, and changing the code changes it
// on the spot. So every control here edits a line in the panel, and the panel
// is the actual shape of what react-three-fiber code looks like.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Sparkles } from 'lucide-react';
import { Fitted, HaloRing, PLATFORM_TOP, ShadowBlob, useCloned } from '../helpers';
import { T } from '../../../utils/fonts';
import DemoShell from './DemoShell';

const DEFAULTS = { speed: 0.8, scale: 1, color: '#34D9B5', wireframe: true };

const SWATCHES = [
  { value: '#34D9B5', label: 'טורקיז' },
  { value: '#5CA7DD', label: 'כחול' },
  { value: '#FFC857', label: 'זהב' },
  { value: '#7CBD4A', label: 'ירוק' },
];

const Shape = ({ speed, scale, color, wireframe, top, reduced }) => {
  const mesh = useRef();
  const ring = useRef();

  useFrame((state, delta) => {
    const m = mesh.current;
    if (!m) return;
    const step = Math.min(delta, 0.05);
    if (!reduced) {
      m.rotation.y += speed * step;
      m.rotation.x += speed * step * 0.25;
    }
    m.scale.setScalar(THREE.MathUtils.damp(m.scale.x, scale, 9, step));
    if (ring.current) {
      ring.current.scale.setScalar(THREE.MathUtils.damp(ring.current.scale.x, scale, 9, step));
      if (!reduced) ring.current.rotation.z += speed * step * 0.6;
    }
    // A slow bob, so the object reads as suspended rather than pinned.
    m.position.y = top + 1.55 + (reduced ? 0 : Math.sin(state.clock.elapsedTime * 1.1) * 0.07);
  });

  return (
    <>
      <mesh ref={mesh} position={[0, top + 1.55, 0]}>
        <icosahedronGeometry args={[0.78, 2]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={wireframe ? 0.55 : 0.22}
          wireframe={wireframe}
          transparent={wireframe}
          opacity={wireframe ? 0.85 : 1}
          roughness={0.35}
          metalness={0.15}
          toneMapped={false}
        />
      </mesh>
      <mesh ref={ring} position={[0, top + 1.55, 0]} rotation={[Math.PI / 2.4, 0, 0]}>
        <torusGeometry args={[1.24, 0.014, 10, 90]} />
        <meshBasicMaterial color={color} transparent opacity={0.5} toneMapped={false} />
      </mesh>
      <pointLight color={color} intensity={2.2} distance={6} decay={2} position={[0, top + 1.55, 0]} />
    </>
  );
};

const Framing = ({ top }) => {
  const { camera, size } = useThree();

  useEffect(() => {
    // Framed for the largest the shape can get: at scale 1.6 the orbit ring is
    // nearly 2 units across, and a camera composed for the default crops it.
    const narrow = size.width < 640;
    camera.position.set(0, top + (narrow ? 2.5 : 2.3), narrow ? 7.1 : 6.4);
    camera.lookAt(0, top + 1.55, 0);
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, top]);

  return null;
};

const Scene = ({ accent, accentInk, reduced, ...shape }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  return (
    <>
      <Framing top={top} />

      <ambientLight intensity={0.5} />
      <hemisphereLight args={['#9DC3E6', '#0A1020', 0.6]} />
      <directionalLight position={[5, 9, 6]} intensity={1.2} />

      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.45} position={[0, top - 0.4, 0]} />

      <Shape top={top} reduced={reduced} {...shape} />
    </>
  );
};

/* ── Demo ─────────────────────────────────────────────────────────────────── */

/** Which code line each control writes to — used to light that line up. */
const LINE_OF = { scale: 0, color: 2, wireframe: 2, speed: 4 };

const SceneDemo = ({ accent, accentInk, reduced = false, onFirstUse, onRun }) => {
  const [state, setState] = useState(DEFAULTS);
  const [edits, setEdits] = useState(0);
  const [touched, setTouched] = useState(null);
  const announced = useRef(false);
  const clearTouch = useRef();

  const edit = useCallback((key, value) => {
    setState((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    setEdits((n) => n + 1);
    setTouched(key);
    if (!announced.current) {
      announced.current = true;
      if (onFirstUse) onFirstUse();
      if (onRun) onRun();
    }
  }, [onFirstUse, onRun]);

  // The highlight is a flash, not a mode: it says "this line just changed".
  useEffect(() => {
    if (touched === null) return undefined;
    clearTimeout(clearTouch.current);
    clearTouch.current = setTimeout(() => setTouched(null), 1400);
    return () => clearTimeout(clearTouch.current);
  }, [touched, edits]);

  useEffect(() => () => clearTimeout(clearTouch.current), []);

  const lines = useMemo(() => [
    `<mesh scale={${state.scale.toFixed(2)}}>`,
    '  <icosahedronGeometry args={[1, 2]} />',
    `  <meshStandardMaterial color="${state.color}"${state.wireframe ? ' wireframe' : ''} />`,
    '</mesh>',
    `useFrame((_, d) => (mesh.rotation.y += ${state.speed.toFixed(2)} * d))`,
  ], [state]);

  const reset = useCallback(() => {
    setState(DEFAULTS);
    setEdits(0);
    setTouched(null);
  }, []);

  return (
    <DemoShell
      camera={{ fov: 42, near: 0.1, far: 60, position: [0, 2.7, 6.4] }}
      scene={
        <Scene
          accent={accent}
          accentInk={accentInk}
          reduced={reduced}
          speed={state.speed}
          scale={state.scale}
          color={state.color}
          wireframe={state.wireframe}
        />
      }
      fallback={
        // No WebGL: show the values themselves, which is what the demo is about.
        <div className="scene-flat" aria-hidden="true">
          <span className="swatch" style={{ background: state.color }} />
          <p>scale {state.scale.toFixed(2)} · speed {state.speed.toFixed(2)}</p>
        </div>
      }
      status={
        <>
          <Sparkles size={16} aria-hidden="true" />
          {edits === 0 ? 'שנו משהו — הסצנה תגיב מיד' : <>ערכתם את הקוד <strong>{edits}</strong> פעמים</>}
        </>
      }
      code={{
        tag: 'REACT + THREE.JS',
        label: 'קוד react-three-fiber שמתאר את הסצנה, מתעדכן לפי הפקדים',
        lines,
        activeLine: touched === null ? null : LINE_OF[touched],
      }}
      controls={
        <div className="scene-controls">
          <label className="scene-row">
            <span>מהירות סיבוב</span>
            <input
              type="range" min="0" max="2" step="0.05" value={state.speed}
              onChange={(e) => edit('speed', Number(e.target.value))}
            />
          </label>

          <label className="scene-row">
            <span>גודל</span>
            <input
              type="range" min="0.6" max="1.6" step="0.05" value={state.scale}
              onChange={(e) => edit('scale', Number(e.target.value))}
            />
          </label>

          <div className="scene-row">
            <span id="scene-color-label">צבע</span>
            <div className="scene-swatches" role="group" aria-labelledby="scene-color-label">
              {SWATCHES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={s.value === state.color ? 'swatch is-on' : 'swatch'}
                  style={{ background: s.value }}
                  aria-label={s.label}
                  aria-pressed={s.value === state.color}
                  onClick={() => edit('color', s.value)}
                />
              ))}
            </div>
          </div>

          <label className="scene-row scene-toggle">
            <span>wireframe</span>
            <input
              type="checkbox"
              checked={state.wireframe}
              onChange={(e) => edit('wireframe', e.target.checked)}
            />
          </label>

          <style>{`
            .scene-controls { display: flex; flex-direction: column; gap: 12px; width: 100%; }
            .scene-row {
              display: flex; align-items: center; justify-content: space-between; gap: 14px;
              font-size: 0.86rem; font-weight: 700; color: ${T.muted};
            }
            .scene-row > span { flex-shrink: 0; }
            .scene-row input[type="range"] {
              flex: 1; min-width: 0; accent-color: ${T.web3d}; cursor: pointer;
              height: 24px;
            }
            .scene-row input[type="range"]:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 4px; }
            .scene-swatches { display: flex; gap: 8px; }
            .scene-swatches .swatch {
              width: 30px; height: 30px; border-radius: 9px; cursor: pointer;
              border: 2px solid transparent; padding: 0;
            }
            .scene-swatches .swatch.is-on { border-color: ${T.text}; transform: scale(1.08); }
            .scene-swatches .swatch:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 3px; }
            .scene-toggle input {
              width: 22px; height: 22px; accent-color: ${T.web3d}; cursor: pointer;
            }
            .scene-flat {
              position: absolute; inset: 0;
              display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px;
              font-family: ${T.fontHud}; font-size: 0.8rem; color: ${T.muted};
            }
            .scene-flat .swatch { width: 84px; height: 84px; border-radius: 20px; }
          `}</style>
        </div>
      }
      hint="כל פקד כאן כותב מחדש שורה בקוד — וזה בדיוק הקוד שכותבים בקורס."
      onReset={reset}
      resetDisabled={edits === 0}
      resetLabel="חזרה לברירת המחדל"
    />
  );
};

export default SceneDemo;
